import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import {
  ErrorStateMatcher,
  ShowOnDirtyErrorStateMatcher,
} from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { maskitoTransform } from '@maskito/core';
import { AutoUnsubscribeComponent } from '@wz-utilities/auto-unsubscribe.component';
import { hasValidator } from '@wz-utilities/form.utilities';
import { isStringNullOrEmpty } from '@wz-utilities/string.utilities';
import { Subject } from 'rxjs';
import { MaskConfiguration } from '../../../../../libs/constants/mask-constants';

@Component({
  selector: 'wz-text-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  standalone: false,
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: TextInputComponent,
    },
    {
      provide: ErrorStateMatcher,
      useExisting: ShowOnDirtyErrorStateMatcher,
    },
  ],
  // tslint:disable-next-line:use-host-property-decorator
  host: {
    '[class.floating]': 'shouldLabelFloat',
    '[id]': 'id',
    '[attr.aria-describedby]': 'describedBy',
  },
})
export class TextInputComponent
  extends AutoUnsubscribeComponent
  implements
    OnInit,
    OnDestroy,
    ControlValueAccessor,
    MatFormFieldControl<string>,
    DoCheck
{
  static nextId = 0;

  @ViewChild('input', { static: false })
  inputRef!: ElementRef;

  errorStateMatcher!: ErrorStateMatcher;
  stateChanges = new Subject<void>();
  focused = false;
  errorState = false;
  describedBy = '';
  id: string;

  @Input()
  public minimumLength?: number;

  @Input()
  public maskConfig!: MaskConfiguration | null;
  @Input()
  public type: string = 'text';

  private _isInteger = false;
  @Input()
  public set isInteger(value: boolean) {
    if (value) {
      this.type = 'number';
    }

    this._isInteger = value;
  }

  get controlType() {
    return 'wz-text-input';
  }

  get empty(): boolean {
    return isStringNullOrEmpty(this.value);
  }

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  private _placeholder!: string;

  @Input()
  get required() {
    return this._required;
  }
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(dis) {
    this._disabled = coerceBooleanProperty(dis);
    this.stateChanges.next();
  }
  private _disabled = false;

  @Output()
  public valueChange: EventEmitter<any> = new EventEmitter<any>();

  protected internalValueChange = new Subject<any>();

  @Input()
  private _value = '';

  public get value(): string {
    return this._value;
  }

  public set value(val: string) {
    this._value = val;

    if (this.minimumLength && val.length < this.minimumLength) {
      return;
    }

    if (this.type === 'number') {
      let numericVal: any = isStringNullOrEmpty(this.value)
        ? null
        : +this.value;
      if (numericVal !== null && this._isInteger) {
        numericVal = Math.trunc(numericVal);
        setTimeout(() => {
          this._value = numericVal.toString();
        }, 0);
      }
      this.propagateChange(numericVal);

      this.internalValueChange.next(numericVal);
      this.valueChange.next(numericVal);
    } else {
      let unmaskedValue = this.value;
      if (this.maskConfig && this.maskConfig.mask) {
        unmaskedValue = this.maskConfig.unmask(unmaskedValue);
      }

      this.propagateChange(unmaskedValue);

      this.internalValueChange.next(unmaskedValue);
      this.valueChange.next(unmaskedValue);
    }

    this.stateChanges.next();
  }

  constructor(
    private focusMonitor: FocusMonitor,
    private elRef: ElementRef,
    @Optional() @Self() @Inject(NgControl) public ngControl: NgControl | any,
  ) {
    super();
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    focusMonitor.monitor(elRef.nativeElement, true).subscribe((origin) => {
      this.focused = !!origin;
      this.stateChanges.next();
    });

    this.id = this.getId();
  }

  ngOnInit() {
    this.required = hasValidator(this.ngControl.control, 'required');
    // handle generic validators
    if ((<any>this).validate) {
      this.ngControl.control.setValidators(
        Validators.compose([
          this.ngControl.control.validator,
          (<any>this).validate.bind(this),
        ]),
      );
    }

    this.ngControl.statusChanges.subscribe((_: any) => {
      this.required = hasValidator(this.ngControl.control, 'required');
    });
  }

  ngDoCheck() {
    this.updateErrorState();
  }

  override ngOnDestroy() {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elRef.nativeElement);
  }

  getId() {
    return `${this.controlType}-${TextInputComponent.nextId++}`;
  }

  focus() {
    this.inputRef.nativeElement.focus();
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.inputRef.nativeElement.focus();
    }
  }

  updateErrorState() {
    this.errorState = this.ngControl.dirty && this.ngControl.invalid;
  }

  /** ControlValueAccessor imp */
  writeValue(value: any) {
    if (value !== undefined) {
      this.internalValueChange.next(value);
      if (this.maskConfig && value !== '' && value !== null) {
        let conformed = maskitoTransform(value, {
          mask: this.maskConfig.mask,
        });

        if (conformed == value) {
          value = conformed;
        }
      }

      this._value = value;
    }
  }

  propagateChange = (_: any) => {};

  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  registerOnTouched() {}
}
